/**
 * Vaults API functions
 */

import { authenticatedRequest } from "./client";

export interface VaultSummary {
  id: number;
  name: string;
  description: string | null;
  primary_owner_user_id: string | null;
  user_role: "VAULT_OWNER" | "VAULT_CO_ADMIN" | "VAULT_MEMBER";
  tree_count: number;
  created_at: string;
  updated_at: string;
}

export interface VaultMember {
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  role: "VAULT_OWNER" | "VAULT_CO_ADMIN" | "VAULT_MEMBER";
  created_at: string;
}

export interface VaultCreate {
  name: string;
  description?: string;
}

export interface VaultUpdate {
  name?: string;
  description?: string;
}

export interface AddVaultMemberRequest {
  user_id: string;
  email: string;
  role: "VAULT_CO_ADMIN" | "VAULT_MEMBER";
}

export interface UpdateVaultMemberRequest {
  role: "VAULT_CO_ADMIN" | "VAULT_MEMBER";
}

/**
 * List all vaults the current user belongs to
 */
export async function listVaults(token: string): Promise<VaultSummary[]> {
  return authenticatedRequest<VaultSummary[]>(
    { method: "GET", url: "/vaults" },
    token
  );
}

/**
 * Get vault details
 */
export async function getVault(
  vaultId: number,
  token: string
): Promise<VaultSummary> {
  return authenticatedRequest<VaultSummary>(
    { method: "GET", url: `/vaults/${vaultId}` },
    token
  );
}

/**
 * Create a new vault
 */
export async function createVault(
  data: VaultCreate,
  token: string
): Promise<VaultSummary> {
  return authenticatedRequest<VaultSummary>(
    {
      method: "POST",
      url: "/vaults",
      data,
    },
    token
  );
}

/**
 * Update vault details
 */
export async function updateVault(
  vaultId: number,
  data: VaultUpdate,
  token: string
): Promise<VaultSummary> {
  return authenticatedRequest<VaultSummary>(
    {
      method: "PUT",
      url: `/vaults/${vaultId}`,
      data,
    },
    token
  );
}

/**
 * List vault members
 */
export async function listVaultMembers(
  vaultId: number,
  token: string
): Promise<VaultMember[]> {
  return authenticatedRequest<VaultMember[]>(
    { method: "GET", url: `/vaults/${vaultId}/members` },
    token
  );
}

/**
 * Add a member to a vault
 */
export async function addVaultMember(
  vaultId: number,
  data: AddVaultMemberRequest,
  token: string
): Promise<VaultMember> {
  return authenticatedRequest<VaultMember>(
    {
      method: "POST",
      url: `/vaults/${vaultId}/members`,
      data,
    },
    token
  );
}

/**
 * Update a vault member's role
 */
export async function updateVaultMember(
  vaultId: number,
  userId: string,
  data: UpdateVaultMemberRequest,
  token: string
): Promise<VaultMember> {
  return authenticatedRequest<VaultMember>(
    {
      method: "PUT",
      url: `/vaults/${vaultId}/members/${userId}`,
      data,
    },
    token
  );
}

/**
 * Remove a member from a vault
 */
export async function removeVaultMember(
  vaultId: number,
  userId: string,
  token: string
): Promise<void> {
  return authenticatedRequest<void>(
    {
      method: "DELETE",
      url: `/vaults/${vaultId}/members/${userId}`,
    },
    token
  );
}
